import {
  setTaxIDType,
  accountTypes,
  getCorrectedMailingAddress,
  getCorrectedResidentialAddress,
  getOriginalMailingAddress,
  getOriginalResidentialAddress,
  parsePhone,
  PhoneNumberType,
  getAppSourceType,
  secondaryCommitMessage,
  applicationStatusTypes,
  fundingTypes
} from '../../shared/utils';
import {
  pollForSubmissionResponse,
  commitConsent,
  loadUser,
  getEnablementStatus,
  populateResidentialAddress,
  appendMailingAddressIfDifferent,
  getAdditionalCustomersExistingAgreements
} from '../../shared/loaders';
import { houseHoldSummitData, unlockRewards } from '../../shared/summit.utils';
import { merge, mergeTyped, isEmpty, withDefault } from '../../../core/util';
import { formatDate } from '../../shared/dateUtils';

import { commitNewInvestmentsBYOP } from '../shared/loaders';
import { DataLoader } from '../../../core/types/data-loader';
import {
  ApplicationState,
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../../core/types/application-state';
import { postAddressValidation } from '../../shared/services/address-validation-service';
import { getCustomerHouseholdDataByTaxId } from '../../shared/services/household-service';
import { UGMAUTMAApplicationService } from '../../shared/services/mutualfund-taxable-ugmautma';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../../analytics/analytics-helper';
import {
  accountTypesAnalytics,
  productTypesAnalytics
} from '../../../analytics/util';
import { getFundAllocationsService } from '../../shared/services/allocation-service';
import { encode_deviceprint as encodeDeviceprint } from '../../../rsa-fingerprint';
import { retrieveFilterAndAssignAppID } from '../../shared/services/profile-service';

export const makeLoadProfileData = (): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    let existingProfileAppID = '';
    const userResults = await loadUser(a);

    if (
      userResults?.app?.customer?.primaryEmailAddress &&
      userResults?.authenticated
    ) {
      existingProfileAppID = await retrieveFilterAndAssignAppID(
        userResults?.app?.customer?.primaryEmailAddress,
        accountTypes.UGMAUTMA
      );
    }

    return {
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        accountType: accountTypes.UGMAUTMA,
        app: {
          profileApplicationID: existingProfileAppID
        }
      })
    };
  };

export const handleShoppingFundsAndRewards = async (
  shoppingFunds,
  assetTierID
): Promise<any> => {
  const {
    closedFunds: closedFundReward,
    iClassFunds: iClassFundReward
  } = unlockRewards(assetTierID);

  const tickers = shoppingFunds.map(f => f.ticker);

  const funds = await getFundAllocationsService({ tickers });

  let cleanedFunds = funds;

  if (!closedFundReward)
    cleanedFunds = cleanedFunds.filter(fund => fund.openToNewInvestors);
  if (!iClassFundReward)
    cleanedFunds = cleanedFunds.filter(fund => !fund.iClass);

  const fundsContainer =
    shoppingFunds.length !== cleanedFunds.length
      ? {
          chosenInvestments: cleanedFunds,
          loadedData: { shoppingFunds: [] }
        }
      : {
          loadedData: { shoppingFunds: cleanedFunds }
        };

  return fundsContainer;
};

/* eslint-disable complexity */
export const commitPersonalInformation = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs,
    store: {
      amount,
      enabled,
      app: { customer, minor },
      loadedData = {},
      authenticated
    },
    chosen
  } = appState;

  const {
    ssn = '',
    firstName,
    lastName,
    middleName = '',
    dob,
    citizenship = '',
    minorFirstName,
    minorMiddleName = '',
    minorLastName,
    minorDob,
    minorCitizenship,
    minorSsn
  } = inputs;

  const { shoppingFunds = [] } = loadedData;

  let res;

  if (Array.isArray(chosen)) {
    // user came from "update to iclass" modal
    res = commitNewInvestmentsBYOP(appState);
  } else {
    // proceed with committing PI
    const taxID = enabled ? customer?.taxID : ssn.replace(/[^*0-9]/g, '');
    const minorTaxId = minorSsn?.includes('-')
      ? minorSsn.replace(/[^*0-9]/g, '')
      : minor?.taxID;

    const enablementStatus = await getEnablementStatus(appState);

    const minorHHdata = await getCustomerHouseholdDataByTaxId({
      firstName: withDefault(minorFirstName, minor?.name?.firstName),
      lastName: withDefault(minorLastName, minor?.name?.lastName),
      dateOfBirth: withDefault(minorDob, minor?.dob),
      taxId: withDefault(minorTaxId, minor?.taxID)
    });

    const summitData = houseHoldSummitData(minorHHdata);

    const addAdditionalCustomers = [
      {
        customerType: 'MINOR',
        name: {
          firstName: withDefault(minorFirstName, minor?.name?.firstName),
          middleName:
            withDefault(minorMiddleName, minor?.name?.minorMiddleName) || '',
          lastName: withDefault(minorLastName, minor?.name?.lastName)
        },
        dob: formatDate(withDefault(minorDob, minor?.dob), 'MM/DD/YYYY', ''),
        taxID: withDefault(minorTaxId, minor?.taxID),
        citizenship: withDefault(minorCitizenship, minor?.minorCitizenship)
      }
    ];

    const existingAccountAgreementsResults: any[] = await getAdditionalCustomersExistingAgreements(
      {
        authenticated,
        taxID: minorTaxId,
        accountType: accountTypes.UGMAUTMA
      }
    );

    res = merge(
      {
        amount,
        accountType: accountTypes.UGMAUTMA,
        ...(existingAccountAgreementsResults && {
          existingAgreements: existingAccountAgreementsResults
        }),
        app: {
          customer: {
            name: {
              firstName: withDefault(firstName, customer?.name?.firstName),
              lastName: withDefault(lastName, customer?.name?.lastName),
              middleName:
                withDefault(middleName, customer?.name?.middleName) || ''
            },
            dob: formatDate(withDefault(dob, customer?.dob), 'MM/DD/YYYY'),
            citizenship:
              withDefault(inputs.citizenship, customer?.citizenship) || '',
            taxID,
            taxIDType: setTaxIDType(
              withDefault(citizenship, customer?.citizenship)
            )
          },
          minor: {
            name: {
              firstName: withDefault(minorFirstName, minor?.name?.firstName),
              middleName:
                withDefault(minorMiddleName, minor?.name?.minorMiddleName) ||
                '',
              lastName: withDefault(minorLastName, minor?.name?.lastName)
            },
            dob: formatDate(withDefault(minorDob, minor?.dob), 'MM/DD/YYYY'),
            taxID: minorTaxId,
            taxIDType: setTaxIDType(
              withDefault(minorCitizenship, minor?.minorCitizenship)
            ),
            citizenship: withDefault(minorCitizenship, minor?.minorCitizenship)
          }
        },
        additionalCustomers: addAdditionalCustomers,
        summitData,
        ...(shoppingFunds.length && {
          ...(await handleShoppingFundsAndRewards(
            shoppingFunds,
            summitData.assetTierID
          ))
        })
      },
      enablementStatus
    );
  }
  return res;
};
/* eslint-enable complexity */

const getGoverningState = (minorAddress, customerAddress): string => {
  const { minorSameAddress, minorState, minorGoverningState } = minorAddress;
  const { state: customerState } = customerAddress;
  if (minorSameAddress) return customerState;

  if (!isEmpty(minorState) && minorState !== customerState)
    return minorGoverningState;
  return customerState;
};

// [TODO] @Heather [07/15/2021]: fix complexity
/* eslint-disable complexity */
export const commitAddressValidation = async ({
  inputs,
  store
}): Promise<StateStore> => {
  const { app, additionalCustomers } = store;
  const { customer, minor } = app;
  const residentialStreet1 = withDefault(inputs.residentialStreet1, '');
  const residentialStreet2 = withDefault(inputs.residentialStreet2, '');
  const residentialZip = withDefault(inputs.residentialZip, '');
  const residentialCity = withDefault(inputs.residentialCity, '');
  const residentialState = withDefault(inputs.residentialState, '');
  const {
    mailingAddressDifferent,
    people,
    phone1,
    phone2,
    phone3,
    primaryEmailAddress
  } = inputs;

  const residentialAddress = {
    city: residentialCity.trim(),
    country: 'United States',
    state: residentialState.trim(),
    street1: residentialStreet1.trim(),
    street2: residentialStreet2?.trim(),
    street3: '',
    usaddress: true,
    zip: residentialZip.trim()
  };

  let mailingAddress = {};
  if (mailingAddressDifferent) {
    const mailingStreet1 = withDefault(inputs.mailingStreet1, '');
    const mailingStreet2 = withDefault(inputs.mailingStreet2, '');
    const mailingZip = withDefault(inputs.mailingZip, '');
    const mailingCity = withDefault(inputs.mailingCity, '');
    const mailingState = withDefault(inputs.mailingState, '');

    mailingAddress = {
      city: mailingCity.trim(),
      country: 'United States',
      state: mailingState.trim(),
      street1: mailingStreet1.trim(),
      street2: mailingStreet2?.trim(),
      street3: '',
      usaddress: true,
      zip: mailingZip.trim()
    };
  }

  let addresses = [residentialAddress];
  addresses = appendMailingAddressIfDifferent(
    addresses,
    mailingAddressDifferent,
    mailingAddress
  );

  const phones = [
    { phone: phone1, type: PhoneNumberType.CELLULAR },
    {
      phone: phone2,
      type: PhoneNumberType.DAYTIME
    },
    { phone: phone3, type: PhoneNumberType.EVENING }
  ]
    .filter(({ phone }) => phone)
    .map(({ phone, type }) => parsePhone(phone, type));

  const [
    residentialAddressValid,
    mailingAddressValid
  ] = await postAddressValidation(addresses);

  const residentialAddressValidation =
    residentialAddressValid || residentialAddress;
  const mailingAddressValidation = mailingAddressValid || mailingAddress;

  const minorResidentialAddress = people[0];

  // need to commit email address in the case user has updated it since basic pi
  // otherwise modal action will wipe any user updates to email address
  const res = {
    residentialAddressValidation,
    ...(mailingAddressDifferent && { mailingAddressValidation }),
    mailingAddressDifferent,
    app: {
      customer: {
        ...customer,
        phones,
        primaryEmailAddress
      },
      minor: {
        ...minor,
        residentialAddress: minorResidentialAddress.minorSameAddress
          ? residentialAddress
          : {
              street1: minorResidentialAddress.minorStreet1.trim(),
              street2: minorResidentialAddress.minorStreet2?.trim(),
              state: minorResidentialAddress.minorState.trim(),
              zip: minorResidentialAddress.minorZip.trim(),
              city: minorResidentialAddress.minorCity.trim()
            }
      },
      governingState: getGoverningState(
        minorResidentialAddress,
        residentialAddress
      )
    },
    additionalCustomers: people?.map((person, idx) => ({
      ...additionalCustomers[idx],
      residentialAddress: person?.minorSameAddress
        ? residentialAddress
        : {
            street1: person?.minorStreet1.trim(),
            street2: person?.minorStreet2?.trim(),
            state: person?.minorState.trim(),
            zip: person?.minorZip.trim(),
            city: person?.minorCity.trim()
          },
      sameAddress: withDefault(person?.minorSameAddress, false)
    }))
  };
  return res;
};
/* eslint-enable complexity */

export const commitContactInformation: DataLoader = async ({
  inputs,
  chosen,
  store
}): Promise<StateStore> => {
  const {
    app: { minor },
    additionalCustomers = []
  } = store;

  const { useResidentialAddress, useMailingAddress } = inputs;
  const {
    residentialAddressValidation,
    mailingAddressValidation = {},
    mailingAddressDifferent
  } = store;

  if (chosen?.section === 'addressValidation') {
    return commitAddressValidation({ inputs, store });
  }

  const originalResidentialAddress = getOriginalResidentialAddress(
    residentialAddressValidation
  );
  const correctedResidentialAddress = getCorrectedResidentialAddress(
    residentialAddressValidation
  );
  const residentialAddress = populateResidentialAddress(
    useResidentialAddress,
    originalResidentialAddress,
    correctedResidentialAddress
  );

  const originalMailingAddress = getOriginalMailingAddress(
    mailingAddressValidation
  );
  const correctedMailingAddress = getCorrectedMailingAddress(
    mailingAddressValidation
  );
  let mailingAddress = residentialAddress;
  if (mailingAddressDifferent) {
    if (useMailingAddress === 'useOriginalMailingAddress') {
      const { street1, street2, city, state, zip } = originalMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        corrected: false
      };
    } else {
      const {
        street1,
        street2,
        city,
        state,
        zip,
        zip4
      } = correctedMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        zip4,
        corrected: true
      };
    }
  }

  const res = {
    app: {
      customer: {
        residentialAddress,
        mailingAddress
      },
      minor
    },
    additionalCustomers: additionalCustomers?.map((customer, idx) => ({
      ...additionalCustomers[idx],
      residentialAddress: customer?.sameAddress
        ? residentialAddress
        : customer?.residentialAddress,
      sameAddress: customer?.sameAddress
    }))
  };
  return res;
};

export const createUGMAUTMAApplication: DataLoader = async ({
  inputs,
  store,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (['byop', 'funding', 'info', 'beneficiaries', 'minor'].includes(chosen)) {
    return { inReviewMode: true };
  }

  const { app, consumerName } = store;
  const { customer, termsIDs } = app;
  const { eligibleForTaxWithholding = false } = inputs;
  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());
  const updatedAppState = {
    ...app,
    fundingMethodType: fundingTypes.FUND_LATER,
    eligibleForTaxWithholding,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await UGMAUTMAApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
// specifies loading message when creating account at end
createUGMAUTMAApplication.message = 'Creating your new account';
createUGMAUTMAApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  analyticsDataLayer({
    accountType: accountTypesAnalytics.TAXABLE.UGMA.NEW,
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    clientTier: store?.summitData?.assetTierID?.toString(),
    isComplete: Boolean(flowProps?.analytics?.isComplete)
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
